import { Injectable } from '@angular/core';
import { endpoint } from '@tekman/onmat/constants';
import {
  CenterTeacher,
  Group,
  GroupCreationData,
  GroupDetail,
  ITimePeriod,
  OnmatItinerary,
  PeriodItinerary,
  StudentsGroup,
  TeacherGroup,
} from '@tekman/onmat/models';
import { ApiService } from '@tekman/onmat/service/generic';
import { Observable, of, Subject, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/internal/operators';
import { catchError } from 'rxjs/operators';
import { AlertService } from '@tekman/shared/service';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  constructor(
    private readonly apiService: ApiService,
    private alertService: AlertService
  ) {}

  itinerariesSubject = new Subject();
  itineraries$ = this.itinerariesSubject.asObservable();

  academicYearSubject = new Subject();
  academicYear$ = this.academicYearSubject.asObservable();

  getGroups() {
    return this.apiService.get<any>(endpoint.group.retrieve);
  }

  getUserGroupsByAcademicYear(academicYearId: string): Observable<Group[]> {
    return this.apiService.get<Group[]>(this.apiService.replaceUrl(endpoint.group.userGroupsByYear, '{academicYearId}', academicYearId));
  }

  getGroupsByAcademicYear(academicYearId: string): Observable<GroupDetail[]> {
    return this.apiService.get<GroupDetail[]>(this.apiService.replaceUrl(endpoint.group.groupsByYear, '{academicYearId}', academicYearId));
  }

  getGroupById(groupId: string): Observable<Group> {
    return this.apiService.get<Group>(this.apiService.replaceUrl(endpoint.group.groupById, '{id}', groupId));
  }

  getTeachersGroup(): Observable<TeacherGroup[]> {
    return this.apiService.get<TeacherGroup[]>(endpoint.group.teachers.get);
  }

  getCenterTeachers(academicYearId: string): Observable<CenterTeacher[]> {
    return this.apiService.get<CenterTeacher[]>(
      this.apiService.replaceUrl(endpoint.group.teachers.byAcademicYear, '{academicYearId}', `${academicYearId}`)
    );
  }

  deleteTeacher(teacherId: string): Observable<any> {
    return this.apiService.delete<any>(this.apiService.replaceUrl(endpoint.group.teachers.delete, '{teacherId}', teacherId));
  }

  getGroupItinerary(groupId: string): Observable<OnmatItinerary> {
    return this.apiService.get<OnmatItinerary>(this.apiService.replaceUrl(endpoint.group.itineraries.group, '{groupId}', groupId));
  }

  deleteGroup(groupId: string): Observable<any> {
    return this.apiService.delete<any>(this.apiService.replaceUrl(endpoint.group.classes.delete, '{groupId}', groupId));
  }

  createGroup(group: GroupCreationData): Observable<GroupCreationData> {
    return this.apiService.post<any>(endpoint.group.retrieve, group);
  }

  editGroup(group: GroupCreationData): Observable<GroupCreationData> {
    return this.apiService.put<any>(this.apiService.replaceUrl(endpoint.group.groupById, '{id}', group.id), group);
  }

  createQualificationsFile(groupId: string, timePeriods: string[]): Observable<Blob | undefined> {
    this.alertService.openAlert('', true, 5000, true);

    const url = this.apiService.replaceUrlArray(endpoint.itineraries.qualifications.create, ['{id}'], [groupId]);
    return this.apiService.post(url, timePeriods).pipe(
      switchMap(() => {
        return timer(0, 5000).pipe(
          switchMap(() => this.statusQualificationsFile(groupId)),
          takeWhile(status => !status, true)
        );
      }),
      switchMap(() => this.downloadQualificationsFile(groupId)),
      catchError(() => {
        return of(undefined);
      })
    );
  }

  private statusQualificationsFile(groupId: string) {
    const url = this.apiService.replaceUrlArray(endpoint.itineraries.qualifications.status, ['{id}'], [groupId]);
    return this.apiService.get(url);
  }

  private downloadQualificationsFile(groupId: string): Observable<Blob> {
    const url = this.apiService.replaceUrlArray(endpoint.itineraries.qualifications.download, ['{id}'], [groupId]);
    return this.apiService.getFile(url);
  }

  loadUserProgress(groupId: string): Observable<string> {
    return this.apiService.get<string>(this.apiService.replaceUrl(endpoint.group.progress, '{groupId}', groupId));
  }

  updateUserProgress(selectedGroupId: string, stepId: string): Observable<string> {
    return this.apiService.put<string>(
      this.apiService.replaceUrl(endpoint.group.progress, '{groupId}', selectedGroupId),
      JSON.stringify(stepId)
    );
  }

  updatePeriodItineraries(timePeriods: ITimePeriod[], periodItinerariesId: string, groupId: string): Observable<any[]> {
    return this.apiService.post<ITimePeriod[]>(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.group.itineraries.modify, '{id}', periodItinerariesId),
        '{groupId}',
        groupId
      ),
      timePeriods
    );
  }

  setCurrentStep(groupId: string, stepId: string): Observable<any> {
    return this.apiService.post<any>(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.group.itineraries.setCurrentStep, '{groupId}', groupId),
        '{stepId}',
        stepId
      ),
      {}
    );
  }

  setStepComplete(groupId: string, stepId: string, isCompleted: boolean): Observable<any> {
    return this.apiService.post<any>(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.group.itineraries.setCompletedStep, '{groupId}', groupId),
        '{stepId}',
        stepId
      ),
      { isCompleted }
    );
  }

  setStepHidden(groupId: string, stepId: string, isHidden: boolean): Observable<any> {
    return this.apiService.post<any>(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.group.itineraries.setHiddenStep, '{groupId}', groupId),
        '{stepId}',
        stepId
      ),
      isHidden
    );
  }

  rollbackPeriodItineraries(periodItinerariesId: string, groupId: string): Observable<PeriodItinerary[]> {
    return this.apiService.delete<PeriodItinerary[]>(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.group.itineraries.rollback, '{id}', periodItinerariesId),
        '{groupId}',
        groupId
      )
    );
  }

  reSendInvitationStudents(pendingUsers: string[]): Observable<Array<{ studentId: string; result: number }>> {
    return this.apiService.post<any>(endpoint.group.classes.reSendInvitationStudents, pendingUsers);
  }

  reSendInvitationTeachers(pendingUsers: string[]): Observable<Array<{ teacherId: string; status: number }>> {
    return this.apiService.post<any>(endpoint.group.classes.reSendInvitationTeachers, pendingUsers);
  }

  saveTeachers(teachers: TeacherGroup[]): Observable<CenterTeacher[]> {
    return this.apiService.post<any>(endpoint.group.teachers.get, teachers);
  }

  updatePassword(id: string, password: string): Observable<void> {
    return this.apiService.post<void>(this.apiService.replaceUrl(endpoint.user.updatePassword, '{id}', id), { password });
  }

  updateStudentInfo(id: string, name: string, lastName: string): Observable<void> {
    return this.apiService.put<void>(this.apiService.replaceUrl(endpoint.user.updateInfo, '{id}', id), {
      name,
      lastName,
    });
  }

  deleteStudent(groupId: string, studentId: string): Observable<void> {
    return this.apiService.delete<void>(
      this.apiService.replaceUrlArray(endpoint.group.students.delete, ['{groupId}', '{studentId}'], [groupId, studentId])
    );
  }

  inviteStudents(groupId: string, userNames: string[]): Observable<StudentsGroup[]> {
    return this.apiService.post<StudentsGroup[]>(this.apiService.replaceUrl(endpoint.group.students.invite, '{groupId}', groupId), {
      userNames,
    });
  }

  updateItinerarySteps(itineraryId: string, stepId: string, groupId: string): Observable<void> {
    return this.apiService.put<void>(
      this.apiService.replaceUrlArray(
        endpoint.itineraries.step.update,
        ['{itineraryId}', '{stepId}', '{groupId}'],
        [itineraryId, stepId, groupId]
      ),
      undefined
    );
  }

  deleteItinerarySteps(itineraryId: string, stepId: string, groupId: string): Observable<void> {
    return this.apiService.delete<void>(
      this.apiService.replaceUrlArray(
        endpoint.itineraries.step.update,
        ['{itineraryId}', '{stepId}', '{groupId}'],
        [itineraryId, stepId, groupId]
      )
    );
  }
}
